// - Renders product details including name, image, and price.
// - Displays a "Sale" badge for products categorized under "Sale" with localized text.
// - Wraps the entire tile in a clickable `Link` component for navigation.
// - Uses responsive styling for alignment and spacing within a compact card layout.
// - Integrates with the `Prices` component to display pricing details, including potential discounts.

import Link from '../../atoms/link';
import React, { useEffect, useMemo, useState } from 'react';
import Prices from '../product/product-tile/prices';
import sale from 'public/locales/en/product.json';
import { offer, standalonePrice, swatches } from './type';
import Price from '../product/product-list-algolia/components/Algolia-search-Page/price';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { useCookies } from 'react-cookie';
import PriceMap from '../product/product-list-algolia/components/AlgoliaPrice/priceMap';
import useLocale from 'helpers/hooks/useLocale';

type BasicProduct = {
  name?: string;
  image?: string;
  price?: Record<string, unknown>;
  categoryKey?: {
    [locale: string]: string[];
  };
  categories?: {
    [locale: string]: {
      lvl0?: string[];
      [key: string]: any;
    };
  };
  slug?: string;
  standalonePrice?: standalonePrice[];
  offers?: string[] | unknown;
  swatches?: swatches[];
};

type product = {
  product?: BasicProduct;
};
const PopularProductTile = ({ product }: product) => {
  const { locale } = useLocale();
  const [price, setPrice] = useState<any>(null);
  const [mrrpPrice, setMrrpPrice] = useState<any>(null);
  const [wasPrice, setWasPrice] = useState<any>(null);
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const convertProductNameToUrl = (input: string) => {
    const urlformat = `${input?.toLowerCase()?.replace(/\s+/g, '-')}`;
    const url = urlformat?.endsWith('-') ? urlformat : `${urlformat}-`;
    return url.replace(/,/g, '');
  };
  const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/?.test(navigator?.userAgent);
  const cartDiscount: offer[] = (product?.offers as offer[]) || [];
  const filteredCartDiscount = cartDiscount?.filter((i: offer) => i?.id !== '');
  const isGBP = cookies?.locale_selection?.currency === 'GBP';
  const mrrpPounds = mrrpPrice / Math.pow(10, 2);
  const pricePounds = price / Math.pow(10, 2);
  const discountPercentage = useMemo(() => {
    if (mrrpPrice <= 0 || price <= 0 || mrrpPrice === price || mrrpPrice < price) {
      return 0; // No discount if prices are invalid or no actual discount
    }

    const discount = ((mrrpPrice - price) / mrrpPrice) * 100;
    return Math.round(discount); // Round to the nearest integer
  }, [mrrpPrice, price]);
  const mrrpPoundsDiscount = useMemo(() => {
    if (!mrrpPounds || !pricePounds || mrrpPounds <= 0 || pricePounds <= 0) {
      return null;
    }
    return mrrpPounds - pricePounds;
  }, [mrrpPounds, pricePounds]);
  useEffect(() => {
    if (!product?.standalonePrice || product?.standalonePrice?.length === 0) {
      // Reset the states if no standalonePrice is available
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
      return;
    }

    const filteredPrice = product?.standalonePrice?.[0] as standalonePrice | undefined;

    if (filteredPrice) {
      // Set the states based on the selected currency
      setPrice(isGBP ? filteredPrice?.priceGB : filteredPrice?.priceEU);
      setMrrpPrice(isGBP ? filteredPrice?.mrrpPriceGB : filteredPrice?.mrrpPriceEU);
      setWasPrice(isGBP ? filteredPrice?.wasPriceGB : filteredPrice?.wasPriceEU);
    } else {
      // Reset the states if no price is found
      setPrice(null);
      setMrrpPrice(null);
      setWasPrice(null);
    }
  }, [product?.standalonePrice, cookies?.locale_selection?.currency]);
  return (
    <>
      <div className="flex flex-col items-center justify-between ">
        <SmartLink
          href={`/${product?.categories?.[locale?.replace('_', '-')]?.lvl0?.[0]?.replace(/\s+/g, '-')}/${
            convertProductNameToUrl(product?.name as string) +
            product?.slug +
            '.html' +
            `?variantCode=${product?.swatches?.[0]?.variantCode ?? ''}`
          }`}
        >
          <div className="p-3">
            <div className="relative mb-4 flex items-center justify-center">
              <img src={product?.image} alt={product?.name} className="h-[240px] w-[240px] object-cover " />
              {product?.categoryKey?.['en-GB']?.includes('Sale') && (
                <button className="absolute right-10 top-10 h-[72px] w-[72px] rounded-full bg-[#fc0000] p-5 font-body text-[22px] font-extrabold leading-4 text-white">
                  {sale.saleButton}
                </button>
              )}
              {typeof discountPercentage === 'number' &&
                discountPercentage > 0 &&
                typeof mrrpPoundsDiscount === 'number' &&
                mrrpPoundsDiscount && (
                  <div className="absolute left-0 top-0 z-10 flex h-[20px] min-w-[39px] items-center justify-center gap-8 self-stretch rounded-[2px] border border-[#686868] bg-white px-6 py-8 sm:left-2 sm:top-2 sm:h-[22px] sm:min-w-[46px]">
                    {/* <div className=" h-[20px] min-w-[39px] lg:h-[22px] lg:min-w-[46px]"> */}
                    <p
                      className={`text-center font-body text-11 font-bold not-italic
                 text-[#686868] sm:text-14 ${
                   isIOS
                     ? 'mb-0 pb-0 pt-4 leading-[30px] sm:leading-[30px]'
                     : 'pt-1 leading-[12.1px] sm:leading-[15.4px]'
                 }`}
                    >
                      {mrrpPounds < 200
                        ? `-${discountPercentage}%`
                        : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                            mrrpPoundsDiscount,
                          )}`}
                    </p>
                    {/* </div> */}
                  </div>
                )}
              {filteredCartDiscount && filteredCartDiscount?.length > 0 && (
                <div className="absolute bottom-0 w-full items-center justify-center bg-[#00d195]">
                  <p className="xs:text-16 px-5 py-8 text-center font-body font-medium leading-5  lg:text-18">
                    {filteredCartDiscount?.[0]?.description || ''}
                  </p>
                </div>
              )}
            </div>
            <div className="text-center">
              <p className="font-body text-[14.4px] font-medium leading-[19.2px] sm:text-16">{product?.name}</p>
            </div>
          </div>
        </SmartLink>
        <div className="mb-10 mt-6 flex h-[48px] items-end text-center">
          <p className="font-body font-bold leading-8 text-primary-black">
            {/* Need to Fix */}
            {/* {CurrencyHelpers.formatForCurrency(price ?? '', locale)} */}
            {/* <Price prices={product?.standalonePrice} className="text-28 sm:text-32" /> */}
            <PriceMap price={price} discountedPercentage={discountPercentage} mrrpPrice={mrrpPrice} />
            {/* {(product?.standalonePrice?.[0]?.priceGB / 100).toFixed(2)} */}
          </p>
          {/* <Prices price={product?.price} /> */}
        </div>
      </div>
    </>
  );
};

export default PopularProductTile;
